//src/context/AuthContext.jsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { http } from "../http";
import endpoints from "../api.router.config";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // { id, full_name, roles, agences, all_agences }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAuthenticated = !!user;

  /* ---------------------- fetchMe ---------------------- */
  const fetchMe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(endpoints.auth.me);
      setUser(res.data?.data || null);
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.response?.data?.code;

      // 401 NO_TOKEN : l'utilisateur n'est pas loggé → user = null
      if (status === 401 && code === "NO_TOKEN") {
        setUser(null);
      } else {
        setUser(null);
        setError(
          e?.response?.data?.error ||
            e?.message ||
            "Erreur lors du chargement du profil"
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Appel initial au montage
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  /* ----------------------- actions ----------------------- */

  // Démarre le flow SSO
  const login = useCallback(() => {
    window.location.href = endpoints.auth.login;
  }, []);

  // Logout: POST /auth/logout + reset local
  const logout = useCallback(async () => {
    try {
      await http.post(endpoints.auth.logout);
    } catch {
      // même si ça échoue côté back, on nettoie côté front
    } finally {
      setUser(null);
    }
  }, []);

  /* ----------------- permissions / roles ----------------- */
  // Chez toi "roles" = liste de permissions SSO
  const hasPermission = useCallback(
    (permOrArray) => {
      if (!user || !Array.isArray(user.roles)) return false;
      const perms = new Set(
        user.roles.map((p) => String(p || "").toLowerCase())
      );

      if (Array.isArray(permOrArray)) {
        return permOrArray.some((p) =>
          perms.has(String(p || "").toLowerCase())
        );
      }
      return perms.has(String(permOrArray || "").toLowerCase());
    },
    [user]
  );

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    reloadUser: fetchMe,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
